import { useInput, FieldTitle, InputHelperText, InputProps } from "react-admin";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

/**
 * react-admin date input backed by MUI X <DatePicker> instead of the native
 * <input type="date">. The native input renders inconsistently on Safari
 * (no calendar picker, empty/invalid value when typed via keyboard), whereas
 * the MUI picker is JS-rendered and behaves the same across browsers.
 *
 * Stores the value as a "YYYY-MM-DD" string (no time/timezone) so downstream
 * consumers that do `dayjs.tz(data.date, timezone)` keep working unchanged.
 */
const DatePickerInput = ({
  source,
  label,
  validate,
  defaultValue,
  disabled,
  fullWidth,
  resource,
  ...rest
}: InputProps & {
  fullWidth?: boolean;
  disabled?: boolean;
}) => {
  const { field, fieldState, formState, isRequired } = useInput({
    source,
    validate,
    defaultValue,
    ...rest
  });

  const { error, invalid, isTouched } = fieldState;
  const { isSubmitted } = formState;
  const showError = (isTouched || isSubmitted) && invalid;

  const value = field.value ? dayjs(field.value) : null;

  return (
    <DatePicker
      label={
        <FieldTitle
          label={label}
          source={source}
          resource={resource}
          isRequired={isRequired}
        />
      }
      value={value}
      disabled={disabled}
      format="MM.DD.YYYY"
      onChange={(date) =>
        field.onChange(
          date && dayjs(date).isValid()
            ? dayjs(date).format("YYYY-MM-DD")
            : null
        )
      }
      slotProps={{
        textField: {
          fullWidth,
          margin: "dense",
          size: "small",
          variant: "filled",
          onBlur: field.onBlur,
          error: showError,
          helperText: showError ? (
            <InputHelperText touched={showError} error={error?.message} />
          ) : (
            <p className="MuiFormHelperText-root">&nbsp;</p>
          )
        }
      }}
    />
  );
};

export default DatePickerInput;
